import React, { useContext } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { MapPin } from 'lucide-react';
import { ContentContext } from '../App';

const ServiceAreasSection = () => {
  const { language } = useLanguage();
  const content = useContext(ContentContext);

  const serviceAreas = content?.service_areas || [];

  return (
    <section className="py-16 bg-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <span className="text-amber-400 font-semibold tracking-wide uppercase text-sm">
            {language === 'en' ? 'Coverage Area' : '服务范围'}
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mt-2">
            {language === 'en' ? 'Service Areas' : '服务区域'}
          </h2>
          <p className="text-slate-400 mt-4 text-lg">
            {language === 'en' ? 'Proudly Serving the Greater Toronto Area' : '服务大多伦多地区'}
          </p>
        </div>

        {/* Areas Grid */}
        <div className="flex flex-wrap justify-center gap-4">
          {serviceAreas.map((area, index) => (
            <div
              key={area.id || index}
              className="flex items-center gap-2 bg-slate-800 px-6 py-3 rounded-full hover:bg-slate-700 transition-colors group cursor-default"
            >
              <MapPin className="w-4 h-4 text-amber-400 group-hover:scale-110 transition-transform" />
              <span className="text-white font-medium">
                {language === 'en' ? area.name_en : area.name_zh}
              </span>
            </div>
          ))}
        </div>

        {/* Static Map */}
        <div className="mt-12 rounded-2xl overflow-hidden h-[350px] bg-slate-800 relative">
          {/* Map Background Image */}
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url('https://maps.googleapis.com/maps/api/staticmap?center=30+West+Beaver+Creek+Rd,Richmond+Hill,ON&zoom=11&size=1200x400&scale=2&format=png&maptype=roadmap&style=feature:all|element:geometry|color:0x1e293b&style=feature:all|element:labels.text.fill|color:0x94a3b8&style=feature:all|element:labels.text.stroke|color:0x0f172a&style=feature:road|element:geometry|color:0x334155&style=feature:water|element:geometry|color:0x0f172a&markers=color:0xf59e0b|label:R|43.8561,-79.3853')`,
              filter: 'brightness(0.9)'
            }}
          />
          {/* Fallback styled map visualization */}
          <div className="absolute inset-0 bg-slate-800">
            <svg viewBox="0 0 1200 350" className="w-full h-full" preserveAspectRatio="xMidYMid slice">
              {/* Background */}
              <rect width="1200" height="350" fill="#1e293b"/>
              
              {/* Major roads */}
              <path d="M0 175 L1200 175" stroke="#334155" strokeWidth="8" fill="none"/>
              <path d="M600 0 L600 350" stroke="#334155" strokeWidth="8" fill="none"/>
              <path d="M0 100 L1200 250" stroke="#334155" strokeWidth="4" fill="none"/>
              <path d="M200 0 L200 350" stroke="#334155" strokeWidth="3" fill="none"/>
              <path d="M400 0 L400 350" stroke="#334155" strokeWidth="3" fill="none"/>
              <path d="M800 0 L800 350" stroke="#334155" strokeWidth="3" fill="none"/>
              <path d="M1000 0 L1000 350" stroke="#334155" strokeWidth="3" fill="none"/>
              <path d="M0 80 L1200 80" stroke="#334155" strokeWidth="3" fill="none"/>
              <path d="M0 270 L1200 270" stroke="#334155" strokeWidth="3" fill="none"/>
              
              {/* Highway 404 */}
              <path d="M700 0 L700 350" stroke="#475569" strokeWidth="12" fill="none"/>
              <text x="705" y="30" fill="#64748b" fontSize="12" fontFamily="sans-serif">HWY 404</text>
              
              {/* Highway 7 */}
              <path d="M0 175 L1200 175" stroke="#475569" strokeWidth="10" fill="none"/>
              <text x="50" y="168" fill="#64748b" fontSize="12" fontFamily="sans-serif">HWY 7</text>
              
              {/* Location Labels */}
              <text x="580" y="140" fill="#94a3b8" fontSize="14" fontFamily="sans-serif">Richmond Hill</text>
              <text x="300" y="200" fill="#94a3b8" fontSize="12" fontFamily="sans-serif">Vaughan</text>
              <text x="850" y="140" fill="#94a3b8" fontSize="12" fontFamily="sans-serif">Markham</text>
              <text x="580" y="300" fill="#94a3b8" fontSize="12" fontFamily="sans-serif">Toronto</text>
              <text x="580" y="50" fill="#94a3b8" fontSize="12" fontFamily="sans-serif">Newmarket</text>
              
              {/* Location Pin */}
              <g transform="translate(600, 160)">
                {/* Pin shadow */}
                <ellipse cx="0" cy="25" rx="15" ry="5" fill="rgba(0,0,0,0.3)"/>
                {/* Pin body */}
                <path d="M0 -30 C-15 -30 -20 -15 -20 -5 C-20 10 0 25 0 25 C0 25 20 10 20 -5 C20 -15 15 -30 0 -30 Z" fill="#f59e0b"/>
                {/* Pin inner circle */}
                <circle cx="0" cy="-10" r="8" fill="#1e293b"/>
                {/* R letter */}
                <text x="0" y="-6" fill="#f59e0b" fontSize="10" fontWeight="bold" textAnchor="middle">R</text>
              </g>
              
              {/* Beaver Creek Rd label */}
              <text x="520" y="195" fill="#f59e0b" fontSize="11" fontFamily="sans-serif" fontWeight="bold">30 W Beaver Creek Rd</text>
            </svg>
          </div>
          
          {/* Location Card Overlay */}
          <div className="absolute bottom-4 left-4 bg-slate-900/90 backdrop-blur-sm rounded-xl p-4 border border-slate-700">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center">
                <MapPin className="w-5 h-5 text-slate-900" />
              </div>
              <div>
                <div className="text-white font-semibold text-sm">ROOFTOPPER</div>
                <div className="text-slate-400 text-xs">30 West Beaver Creek Rd, Unit 113</div>
                <div className="text-slate-400 text-xs">Richmond Hill, ON</div>
              </div>
            </div>
          </div>
          
          {/* Gradient overlay */}
          <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-slate-900/30 to-transparent" />
        </div>
      </div>
    </section>
  );
};

export default ServiceAreasSection;
